import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./capability-02.css";
import unifiedKnowledgeVideo from "../../assets/capability/Calendar & Booking Helper.mp4";
import { useLanguage } from "../../i18n/LanguageContext";

gsap.registerPlugin(ScrollTrigger);

export default function Capability02() {
  const rootRef = useRef(null);
  const { tr } = useLanguage();

  useEffect(() => {
    const ctx = gsap.context(() => {
      const root = rootRef.current;
      if (!root) return;

      const isMobile = window.innerWidth <= 768;

      // Refresh ScrollTrigger saat resize
      const handleResize = () => {
        ScrollTrigger.refresh();
      };
      window.addEventListener("resize", handleResize);

      // === FADE SCROLL HINT (INTRO) ===
      // Adapted from provided logic
      gsap.to(".cap02-scroll", {
        autoAlpha: 0,
        duration: 0.2,
        scrollTrigger: {
          trigger: document.body,
          start: "top top",
          end: "top top-=1",
          toggleActions: "play none reverse none",
        },
      });

      // === LETTER SCROLL ANIMATION ===
      // Adapted from provided logic
      const hcList = root.querySelector(".cap02-list");
      if (hcList && !isMobile) {
        gsap.to(".cap02-letter", {
          yPercent: 100,
          ease: "power1.inOut",
          scrollTrigger: {
            trigger: hcList,
            start: "33.33% bottom",
            end: "100% 80%",
            scrub: 1,
          },
          stagger: {
            each: 0.05,
            from: "random",
          },
        });
      }

      // === SLIDE PPT STYLE (like capability.jsx) ===
      // Set initial state
      gsap.set(root, { autoAlpha: 1, y: 0 });

      if (hcList) {
        gsap.set(hcList, { opacity: 1, y: 0 });
      }

      // Mobile: Skip pinning and complex fade animations
      if (isMobile) {
        // Mobile: no title animation
        
        const slideHeight = window.innerHeight;

        // Pin section for full viewport height (like PowerPoint)
        ScrollTrigger.create({
          trigger: root,
          start: "top top",
          end: `+=${slideHeight}`,
          pin: root,
          pinSpacing: true,
          scrub: false,
        });
      } else {
        // Desktop: Full pinning and fade animations
      const slideHeight = window.innerHeight;

      // Pin section for full viewport height (like PowerPoint)
      ScrollTrigger.create({
        trigger: root,
        start: "top top",
        end: `+=${slideHeight}`,
        pin: root,
        pinSpacing: true,
        scrub: false,
      });

      // Fade in animation when section enters viewport (support scroll up)
        // Only fade content, not background
        if (hcList) {
      gsap.fromTo(
            hcList,
            { opacity: 0, y: 30 },
        {
              opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: root,
            start: "top 80%",
            end: "top 20%",
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
        }

      // Fade out animation when section exits (support scroll up)
        // Only fade content, keep background visible to prevent black screen
        if (hcList) {
      gsap.fromTo(
            hcList,
            { opacity: 1, y: 0 },
        {
              opacity: 0,
          y: -30,
          duration: 1,
          ease: "power2.in",
          scrollTrigger: {
            trigger: root,
            start: "bottom top",
            end: `bottom+=${slideHeight * 0.5} top`,
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
        }
      }

      // Refresh setelah setup
      ScrollTrigger.refresh();

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div style={{ backgroundColor: "#D8D8D8" }}>
      <section id="solutions-02" className="cap02-section" ref={rootRef}>
        

        <div className="cap02-header">
          <ul className="cap02-list cap02-list-horizontal">
          <li>
              {tr("Calendar", "Kalender").split("").map((char, i) => (
                <span key={i} className="cap02-letter">
                <span>{char}</span>
                <span>{char}</span>
              </span>
            ))}
          </li>

          <li>
              {"&".split("").map((char, i) => (
                  <span key={i} className="cap02-letter">
                  <span>{char}</span>
                  <span>{char}</span>
                </span>
              ))}
          </li>

          <li>
              {tr("Booking", "Booking").split("").map((char, i) => (
                <span key={i} className="cap02-letter">
                <span>{char}</span>
                <span>{char}</span>
              </span>
            ))}
          </li>

          <li>
              {tr("Helper", "Helper").split("").map((char, i) => (
                <span key={i} className="cap02-letter">
                <span>{char}</span>
                <span>{char}</span>
              </span>
            ))}
          </li>
        </ul>
        </div>
        
        <p className="cap02-paragraph">
          {tr(
            "Let AI manage your schedule - propose meeting times, send reminders, and even reschedule stuff for you",
            "Biarkan AI mengelola jadwalmu — mengusulkan waktu meeting, mengirim reminder, bahkan reschedule untukmu"
          )}
        </p>
        <p className="cap02-paragraph cap02-note">
          {tr(
            "Useful for professional service providers or content creators managing client calls or collabs",
            "Berguna untuk professional service provider atau content creator yang mengelola call klien atau kolaborasi"
          )}
        </p>
        <p className="cap02-num cap02-num-bottom">(02)</p>
        <video
          className="cap02-video"
          src={unifiedKnowledgeVideo}
          autoPlay
          loop
          muted
          playsInline
        />
      </section>
    </div>
  );
}
